import { CheckedState } from "@centricsoftware/design-system";
import { FIELD_PERMISSIONS_OPTIONS_EDITABLE_CREATE_ONLY, FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY, FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY, FieldPermissionsOptions, FormFieldKeyMap, IFormField, REMEMBER_LAST_VALUE_OPTIONS_ALWAYS, REMEMBER_LAST_VALUE_OPTIONS_NONE, REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING, REQUEST_MODULE, REQUEST_OPERATION, RememberLastValueOptions, createFormData, getDomainFormInfoForNodeType, postFormData, setIsNewFormBuilder } from "c8-platform";
import JsonResponse, { JsonResponseData } from "csi/comm/JsonResponse";
import { callConstructFieldsOnWriterAndSubmit, getLegacyFormFieldKeysByNodeType } from "csi/middleware/form/formUtility";
import { routerStore } from "csi/state/rootStore";
import { C8Node, C8NodeType, ICache, cnl } from "csi/typings";
import { arrayMove } from "csi/utility/arrayUtility";
import { ILocaleProvider } from "designSystem/locale";
import { cloneDeep, compact, isEqual, orderBy, uniqueId } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ChangeEvent, MouseEvent, createContext, useContext } from "react";
import { DropTargetMonitor } from "react-dnd";
import { FORM_DEFINITION_ATTRIBUTE_DRAG_TYPE } from "./EditFormDefinitionAttributesList";
import { SelectableFormFieldsDialog } from "./SelectableFormFieldsDialog";

// TODO: code to support attribute groups in 7.11 --
// /** attribute group map */
// readonly attributeGroupMap: AttributeGroupMap;

/** list of all configurable form fields, account for search query TODO: we might use this for revision classes */
// @computed get transientAttributeGroupMap(): AttributeGroupMap {
// 	const { transientOrderedFieldIds, searchQuery } = this;
// 	const attributeGroupMap = {};
// 	for (const key in this.attributeGroupMap) {
// 		const existingFields = this.attributeGroupMap[key];
// 		const filteredFields = existingFields.filter((field) => {
// 			return !transientOrderedFieldIds.includes(field.id) && field.label.toLowerCase().includes(searchQuery.toLowerCase());
// 		});
// 		attributeGroupMap[key] = orderBy(filteredFields, "label", "asc");
// 	}
// 	return attributeGroupMap;
// }
// --

export type AttributeGroupMap = Record<string, IFormField[]>;

export class FormDefinitionStore {
	/** active field id */
	@observable activeFieldId: string | null = null;

	/** cache */
	cache: ICache;

	/** form definition config */
	readonly formDefinitionConfigNode: C8Node;

	/** form definition field nodes */
	@observable.ref formDefinitionFieldNodes: C8Node[] = [];

	/** map of form field handling */
	@observable formFieldHandlingMap: Record<string, string> = {};

	/** map of form field keys to form fields */
	@observable formFieldKeyMap: FormFieldKeyMap;

	/** copy of form field key map */
	@observable transientFormFieldKeyMap: FormFieldKeyMap;

	/** is active field drawer open */
	@observable isActiveFieldDrawerOpen: boolean = false;

	/** is classic form builder selected */
	@observable isNewFormBuilder: boolean = false;

	/** is custom field dialog open */
	@observable isCustomFieldDialogOpen: boolean = false;

	/** is form definition mode */
	@observable isEditFormDefinitionMode: boolean = false;

	/** is form builder loading */
	@observable isLoading: boolean = false;

	/** locale provider */
	readonly loc: ILocaleProvider;

	/** is migration in progress */
	@observable isMigrationInProgress: boolean = false;

	/** search query */
	@observable searchQuery: string = "";

	/** show boundary */
	showBoundary: (error: Error) => void;

	/** show switching to classic form builder warning dialog */
	@observable showSwitchingToClassicFormBuilderWarningDialog: boolean = false;

	/** ordered list of form fields */
	@observable transientOrderedFieldIds: string[] = [];

	/** node type */
	readonly formType: C8NodeType;

	/** render key */
	@observable renderKey: string = uniqueId();

	/** active field */
	get activeField(): IFormField | null {
		const { activeFieldId, orderedFormFields } = this;
		return activeFieldId ? orderedFormFields.find((field) => field.id === activeFieldId) : null;
	}

	/** Checks if the form fields have been reordered. */
	get isDirty(): boolean {
		return !isEqual(this.savedOrderedFieldIds, this.transientOrderedFieldIds);
	}

	/** is edit permissions editable on create form only disabled */
	get isEditPermissionsEditableOnCreateFormOnlyDisabled(): boolean {
		return this.canDeleteField(this.activeField);
	}

	/** edit permissions radio value */
	get editPermissionsRadioValue(): FieldPermissionsOptions {
		const { activeField } = this;
		if (activeField.createOnly) {
			return FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY;
		}
		return activeField.transientInfo.fieldPermission;
	}

	/** list of all configurable form fields, account for search query */
	@computed get attributes(): IFormField[] {
		const { formFieldKeyMap, searchQuery, transientOrderedFieldIds } = this;
		const sortedAttributes = orderBy(Object.values(formFieldKeyMap), (field) => field.label.toLowerCase(), "asc");
		const filteredAttributes = sortedAttributes.filter((field) => {
			return !transientOrderedFieldIds.includes(field.id) && field.label.toLowerCase().includes(searchQuery.toLowerCase());
		});
		return filteredAttributes;
	}

	/** edit permissions resource */
	get editPermissionsResource(): string {
		const { activeField } = this;
		if (activeField.transientInfo.readOnly) {
			return "ReadOnly";
		}
		if (activeField.formOnly) {
			return "FormOnly";
		}
		if (activeField.createOnly) {
			return "CreateOnly";
		}
		return "Editable";
	}

	/** Retrieves the array of field values of the form definition nodes. */
	get formDefinitionFieldCnls(): cnl[] {
		return this.formDefinitionFieldNodes.map((n) => n.FieldValue);
	}

	/** computes the ordered list of form fields */
	get orderedFormFields(): IFormField[] {
		// compact fields to remove fields that are filtered out by the server
		const fields = compact(this.transientOrderedFieldIds.map((id) => this.transientFormFieldKeyMap[id]));
		return fields;
	}

	/** original ordered list of form fields */
	get savedOrderedFieldIds(): string[] {
		return this.formDefinitionFieldNodes.map((node) => node.$Name);
	}

	/** is create only checkbox checked */
	get isCreateOnlyCheckboxChecked(): boolean {
		return this.activeField.createOnly;
	}

	/** checks if the form field key map is dirty */
	get isDirtyFormFieldKeyMap(): boolean {
		return Object.keys(this.transientFormFieldKeyMap).length > 0;
	}

	/** is Always remember last value checkbox checked */
	get isAlwaysRememberLastValueCheckboxChecked(): boolean {
		return this.activeField.transientInfo.persistValue === REMEMBER_LAST_VALUE_OPTIONS_ALWAYS;
	}

	/** is Always remember last value checkbox disabled */
	get isAlwaysRememberLastValueCheckboxDisabled(): boolean {
		return this.activeField.transientInfo.persistValue === REMEMBER_LAST_VALUE_OPTIONS_NONE;
	}

	/** remember last value radio value */
	get rememberLastValueRadioValue(): RememberLastValueOptions {
		const { persistValue } = this.activeField.transientInfo;
		if (persistValue === REMEMBER_LAST_VALUE_OPTIONS_ALWAYS) {
			return REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING;
		}
		return persistValue;
	}

	/** on save form definition */
	@action onSaveFormDefinition = async (): Promise<void> => {
		await this.saveFieldSelection();
		await this.saveFormFieldKeyMap();
		await this.fetchAndSetOrderedFormFields();
		runInAction(() => {
			this.setIsEditFormDefinitionMode(false);
			this.searchQuery = "";
		});
	};

	/** on search change */
	@action onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
		this.searchQuery = e.target.value;
	};

	/** on cancel form definition */
	@action onCancelFormDefinition = (): void => {
		this.resetTransientOrderedFields();
		this.resetFormFieldKeyMap();
		this.setIsEditFormDefinitionMode(false);
		this.searchQuery = "";
	};

	/** Reorders the form fields based on drag and drop indices. */
	@action orderFields = (
		field: IFormField,
		monitor: DropTargetMonitor,
		targetIndex: number,
	): void => {
		if (monitor.didDrop()) {
			return;
		}

		const dropType = monitor.getItemType();
		const { transientOrderedFieldIds } = this;

		if (dropType === FORM_DEFINITION_ATTRIBUTE_DRAG_TYPE) {
			transientOrderedFieldIds.splice(targetIndex, 0, field.id);
		} else {
			const dragIndex = transientOrderedFieldIds.indexOf(field.id);
			arrayMove(transientOrderedFieldIds, dragIndex, targetIndex > dragIndex ? targetIndex - 1 : targetIndex);
		}
	};

	/** add attribute to end of list */
	@action addAttributeToEnd = (field: IFormField): void => {
		this.transientOrderedFieldIds.push(field.id);
	};

	/** check if field can be deleted, remove delete button for form only and create only attributes */
	canDeleteField = (field: IFormField): boolean => {
		return !field.formOnly && !field.createOnly;
	};

	/** delete field */
	@action deleteField = (field: IFormField): void => {
		const { transientOrderedFieldIds, updateRenderKey } = this;
		const sourceIndex = transientOrderedFieldIds.indexOf(field.id);
		this.transientOrderedFieldIds.splice(sourceIndex, 1);
		updateRenderKey();
	};

	/** move field down */
	@action moveFieldDown = (field: IFormField): void => {
		const { transientOrderedFieldIds, updateRenderKey } = this;
		const sourceIndex = transientOrderedFieldIds.indexOf(field.id);
		const targetIndex = sourceIndex + 1;
		arrayMove(transientOrderedFieldIds, sourceIndex, targetIndex);
		updateRenderKey();
	};

	/** move field up */
	@action moveFieldUp = (field: IFormField): void => {
		const { transientOrderedFieldIds, updateRenderKey } = this;
		const sourceIndex = transientOrderedFieldIds.indexOf(field.id);
		const targetIndex = sourceIndex - 1;
		arrayMove(transientOrderedFieldIds, sourceIndex, targetIndex);
		updateRenderKey();
	};

	/** set active field id */
	@action setActiveFieldId = (field: IFormField | null): void => {
		this.activeFieldId = field ? field.id : null;
	};

	/** remove active field id */
	@action removeActiveFieldId = (): void => {
		this.setActiveFieldId(null);
	};

	/** set active field drawer open */
	@action setActiveFieldDrawerOpen = (open: boolean): void => {
		this.isActiveFieldDrawerOpen = open;
	};

	/** open active field drawer */
	@action openActiveFieldDrawer = (): void => {
		this.setActiveFieldDrawerOpen(true);
	};

	/** handle form field click */
	@action handleFormFieldClick = (field: IFormField): void => {
		this.openActiveFieldDrawer();
		this.setActiveFieldId(field);
	};

	/** handle selectable component click */
	@action onSelectableComponentClick = (e: MouseEvent<HTMLElement>, field: IFormField): void => {
		e.stopPropagation();
		this.handleFormFieldClick(field);
	};

	/** close active field drawer */
	@action closeDrawerAndRemoveActiveField = (): void => {
		this.setActiveFieldDrawerOpen(false);
		this.removeActiveFieldId();
	};

	/** reset form field key map */
	@action resetFormFieldKeyMap = (): void => {
		this.transientFormFieldKeyMap = observable<FormFieldKeyMap>(cloneDeep(this.formFieldKeyMap), {}, { deep: true });
	};

	/** Saves the selected attributes from the dialog. */
	@action saveFieldSelection = async (): Promise<void> => {
		const { cache, transientOrderedFieldIds } = this;
		const { SetupSettings } = this.formDefinitionConfigNode;
		try {
			await callConstructFieldsOnWriterAndSubmit(SetupSettings, transientOrderedFieldIds);
		} catch (ex) {
			console.warn("Saving Form Fields failed.");
		}
	};

	/** set is form definition mode */
	@action setIsEditFormDefinitionMode = (isEditFormDefinitionMode: boolean): void => {
		this.isEditFormDefinitionMode = isEditFormDefinitionMode;
		if (isEditFormDefinitionMode) {
			routerStore.setCenterFloatComponent(SelectableFormFieldsDialog, {
				store: this,
			});
		} else {
			routerStore.setCenterFloatComponent(null);
		}
	};

	/** Sets the loading state of the form builder. */
	@action setLoading = (loading: boolean): void => {
		this.isLoading = loading;
	};

	/** Resets the ordered fields to their original state. */
	@action resetTransientOrderedFields = (): void => {
		this.transientOrderedFieldIds = this.savedOrderedFieldIds.slice();
	};

	/** set show switching to classic form builder warning dialog */
	@action setShowSwitchingToClassicFormBuilderWarningDialog = (show: boolean): void => {
		this.showSwitchingToClassicFormBuilderWarningDialog = show;
	};

	/** switch back to classic form builder */
	@action switchBackToClassicFormBuilder = (): void => {
		this.isNewFormBuilder = false;
		this.setShowSwitchingToClassicFormBuilderWarningDialog(false);
		setIsNewFormBuilder(this.formDefinitionConfigNode.$URL, false);
	};

	/** Toggle the classic form builder optimistically. */
	@action toggleClassicFormBuilder = async (): Promise<void> => {
		const { fetchAndSetOrderedFormFields, formDefinitionConfigNode, formType, init, isNewFormBuilder, setShowSwitchingToClassicFormBuilderWarningDialog } = this;
		const { $URL, SetupSettings } = formDefinitionConfigNode;
		if (!isNewFormBuilder) {
			this.isMigrationInProgress = true;
			// run the migration if switching to new form builder
			const formFieldKeys = await getLegacyFormFieldKeysByNodeType(formType);
			if (!formFieldKeys) {
				runInAction(() => {
					this.isMigrationInProgress = false;
				});
				return;
			}
			try {
				await callConstructFieldsOnWriterAndSubmit(SetupSettings, formFieldKeys);
				// now that forms have been migrated, need to fetch again
				await fetchAndSetOrderedFormFields();
			} catch (ex) {
				console.warn("Migrating Form Fields failed.");
			}
			runInAction(() => {
				this.isNewFormBuilder = true;
			});
			await init();
			runInAction(() => {
				this.isMigrationInProgress = false;
			});
			await setIsNewFormBuilder($URL, true);
		} else {
			// show warning dialog if switching to classic form builder
			setShowSwitchingToClassicFormBuilderWarningDialog(true);
		}
	};

	/** update render key */
	@action updateRenderKey = (): void => {
		this.renderKey = uniqueId();
	};

	/** Initializes the form definition store by fetching and setting up form data. */
	fetchAndSetOrderedFormFields = async (): Promise<void> => {
		this.setLoading(true);
		try {
			const formData = createFormData({
				[REQUEST_MODULE]: "Form",
				[REQUEST_OPERATION]: "GetReorderedFormByNodeType",
				NodeType: this.formType,
				bSetupSettings: true,
			});
			const response = await postFormData<JsonResponseData>(formData);
			const jsonResponse = new JsonResponse(response);
			runInAction(() => {
				this.formDefinitionFieldNodes = jsonResponse.getResultNodes();
				// manually set the transient ordered field ids, because they are modified in real-time
				this.transientOrderedFieldIds = this.formDefinitionFieldNodes.map((node) => node.$Name);
			});
		} catch (ex) {
			console.warn("Getting Form Fields from Java memory failed.");
		}

		this.setLoading(false);
	};

	/** on edit permissions change */
	@action onEditPermissionsChange = (value: string): void => {
		const { activeField } = this;
		activeField.transientInfo.fieldPermission = value;
		if (value === FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY) {
			activeField.transientInfo.readOnly = true;
			activeField.formOnly = false;
			activeField.createOnly = false;
		} else {
			activeField.transientInfo.readOnly = false;
		}
		if (value === FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY) {
			activeField.formOnly = true;
		} else {
			activeField.formOnly = false;
		}
	};

	/** on create only checkbox change */
	@action onCreateOnlyCheckboxChange = (): void => {
		const { activeField } = this;
		const { transientInfo } = this.activeField;
		const { fieldPermission } = transientInfo;
		if (fieldPermission === FIELD_PERMISSIONS_OPTIONS_EDITABLE_CREATE_ONLY) {
			transientInfo.fieldPermission = FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY;
			activeField.createOnly = false;
		} else {
			transientInfo.fieldPermission = FIELD_PERMISSIONS_OPTIONS_EDITABLE_CREATE_ONLY;
			activeField.createOnly = true;
		}
	};

	/** on persist value radio change */
	@action onPersistValueRadioChange = (value: RememberLastValueOptions): void => {
		this.activeField.transientInfo.persistValue = value;
	};

	/** on persist value always checkbox change */
	@action onPersistValueAlwaysCheckboxChange = (checked: CheckedState): void => {
		this.activeField.transientInfo.persistValue = REMEMBER_LAST_VALUE_OPTIONS_ALWAYS;
	};

	/** on required checkbox change */
	@action onRequiredCheckboxChange = (): void => {
		this.activeField.transientInfo.required = !this.activeField.transientInfo.required;
	};

	/** only include fields that are different between the form field key map and the transient form field key map */
	@computed get formFieldKeyMapDiff(): FormFieldKeyMap {
		const { formFieldKeyMap, transientFormFieldKeyMap } = this;
		const formFieldDiffMap: FormFieldKeyMap = {};
		for (const fieldKey in transientFormFieldKeyMap) {
			const transientField = transientFormFieldKeyMap[fieldKey];
			const formFieldKeyMapField = formFieldKeyMap[fieldKey];
			if (!isEqual(transientField, formFieldKeyMapField)) {
				formFieldDiffMap[fieldKey] = transientField;
			}
		}
		return formFieldDiffMap;
	};

	/** save form field key map */
	saveFormFieldKeyMap = async (): Promise<void> => {
		this.setLoading(true);
		try {
			const formData = createFormData({
				[REQUEST_MODULE]: "Form",
				[REQUEST_OPERATION]: "SaveFormFieldKeyMap",
				FormFieldKeyMap: JSON.stringify(this.formFieldKeyMapDiff),
				FormType: this.formType,
			});
			const response = await postFormData<FormFieldKeyMap>(formData);
			runInAction(() => {
				this.formFieldKeyMap = response;
			});
		} catch (ex) {
			console.warn("saveFormFieldKeyMap: Getting Form Fields from Java memory failed.");
		}

		this.setLoading(false);
	};

	/** generate form field key */
	generateFormFieldKey = (field: IFormField): string => {
		return field.id;
	};

	/** update form field key map */
	@action fetchFormFieldKeyMap = async (formFieldKeyMap?: FormFieldKeyMap): Promise<void> => {
		if (!formFieldKeyMap) {
			formFieldKeyMap = await getDomainFormInfoForNodeType(this.formType);
		}
		runInAction(() => {
			this.formFieldKeyMap = formFieldKeyMap;
		});
	};

	/** init by setting the form field key map and resetting the transient form field key map */
	init = async (formFieldKeyMap?: FormFieldKeyMap): Promise<void> => {
		await this.fetchFormFieldKeyMap(formFieldKeyMap);
		await this.resetFormFieldKeyMap();
		this.fetchAndSetOrderedFormFields();
	};

	/** comply with useLocalStore typing, no cleanup needed */
	onCleanup = (): void => {};

	constructor(
		cache: ICache,
		formDefinitionConfigNode: C8Node,
		formFieldKeyMap: FormFieldKeyMap,
		loc: ILocaleProvider,
		formType: string,
		isNewFormBuilder: boolean,
	) {
		this.loc = loc;
		this.cache = cache;
		this.formDefinitionConfigNode = formDefinitionConfigNode;
		this.formType = formType;
		this.isNewFormBuilder = isNewFormBuilder;
		this.init(formFieldKeyMap);
		makeObservable(this);
	}
}

export const FormDefinitionStoreContext = createContext<FormDefinitionStore>(undefined);
export const useFormDefinitionStore = (): FormDefinitionStore => useContext(FormDefinitionStoreContext);